import { useState, useEffect, useCallback } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';

const API = 'https://melvo-backend-production.up.railway.app';

const styles = {
  page: {
    minHeight: '100vh',
    backgroundColor: '#060608',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    fontFamily: 'Geist, sans-serif',
    padding: '24px',
  },
  card: {
    backgroundColor: '#0c0c10',
    border: '1px solid #1a1a24',
    borderRadius: '16px',
    padding: '48px 40px',
    width: '100%',
    maxWidth: '420px',
  },
  logo: {
    color: '#00e5a0',
    fontSize: '22px',
    fontWeight: '700',
    letterSpacing: '-0.5px',
    marginBottom: '40px',
    display: 'block',
  },
  heading: {
    color: '#f0f0f8',
    fontSize: '24px',
    fontWeight: '600',
    marginBottom: '8px',
    lineHeight: '1.3',
  },
  subtext: {
    color: '#6a6a84',
    fontSize: '14px',
    marginBottom: '32px',
    lineHeight: '1.6',
  },
  label: {
    display: 'block',
    color: '#6a6a84',
    fontSize: '13px',
    marginBottom: '8px',
    fontWeight: '500',
  },
  input: {
    width: '100%',
    backgroundColor: '#060608',
    border: '1px solid #1a1a24',
    borderRadius: '8px',
    padding: '12px 16px',
    color: '#f0f0f8',
    fontSize: '16px',
    fontFamily: 'Geist, sans-serif',
    outline: 'none',
    boxSizing: 'border-box',
    marginBottom: '16px',
  },
  button: {
    width: '100%',
    backgroundColor: '#00e5a0',
    color: '#060608',
    border: 'none',
    borderRadius: '8px',
    padding: '13px',
    fontSize: '15px',
    fontWeight: '600',
    fontFamily: 'Geist, sans-serif',
    cursor: 'pointer',
    marginTop: '8px',
  },
  buttonDisabled: {
    opacity: 0.5,
    cursor: 'not-allowed',
  },
  otpRow: {
    display: 'flex',
    gap: '10px',
    marginBottom: '16px',
  },
  otpInput: {
    flex: 1,
    backgroundColor: '#060608',
    border: '1px solid #1a1a24',
    borderRadius: '8px',
    padding: '14px 0',
    color: '#f0f0f8',
    fontSize: '22px',
    fontFamily: 'Geist, sans-serif',
    fontWeight: '600',
    textAlign: 'center',
    outline: 'none',
    width: '100%',
  },
  error: {
    color: '#ff6b6b',
    fontSize: '13px',
    marginBottom: '12px',
  },
  resend: {
    background: 'none',
    border: 'none',
    color: '#00e5a0',
    fontSize: '13px',
    cursor: 'pointer',
    fontFamily: 'Geist, sans-serif',
    padding: 0,
    marginTop: '12px',
    display: 'block',
  },
  resendDisabled: {
    color: '#6a6a84',
    cursor: 'default',
  },
  support: {
    marginTop: '32px',
    paddingTop: '24px',
    borderTop: '1px solid #1a1a24',
    color: '#6a6a84',
    fontSize: '13px',
    textAlign: 'center',
  },
  supportLink: {
    color: '#00e5a0',
    textDecoration: 'none',
  },
};

// Step 1: Validating claim token (auto-runs on mount)
// Step 2: Phone entry (after token validates)
// Step 3: OTP entry (after OTP sent)

export default function ClaimPage() {
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();

  const [step, setStep] = useState('validating'); // validating | phone | otp | error

  const [phone, setPhone] = useState('');
  const [otp, setOtp] = useState(['', '', '', '', '', '']);
  const [loading, setLoading] = useState(false);
  const [errorMsg, setErrorMsg] = useState('');
  const [resendCooldown, setResendCooldown] = useState(0);

  const token = searchParams.get('token');

  const validateToken = useCallback(async () => {
    try {
      const res = await fetch(`${API}/auth/claim/validate`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ token }),
      });
      const data = await res.json();
      if (!res.ok || !data.valid) {
        setStep('error');
        setErrorMsg(
          data.error === 'TOKEN_EXPIRED'
            ? 'This link has expired. Please contact hello@melvo.io for a new one.'
            : data.error === 'TOKEN_USED'
            ? 'This link has already been used. Sign in at app.melvo.io/login.'
            : 'This link is invalid. Please use the link from your welcome email or contact hello@melvo.io.'
        );
        return;
      }
      setStep('phone');
    } catch {
      setStep('error');
      setErrorMsg('Unable to verify your link. Please try again or contact hello@melvo.io.');
    }
  }, [token]);

  // Auto-validate claim token on mount
  useEffect(() => {
    if (!token) {
      setStep('error');
      setErrorMsg('This link is missing a token. Please use the link from your welcome email.');
      return;
    }
    validateToken();
  }, [token, validateToken]);

  // Resend cooldown timer
  useEffect(() => {
    if (resendCooldown <= 0) return;
    const t = setTimeout(() => setResendCooldown(c => c - 1), 1000);
    return () => clearTimeout(t);
  }, [resendCooldown]);

  async function requestOtp() {
    setErrorMsg('');
    const cleaned = phone.replace(/\D/g, '');
    if (cleaned.length < 10) {
      setErrorMsg('Enter a valid US phone number.');
      return;
    }
    setLoading(true);
    try {
      const res = await fetch(`${API}/auth/otp/request`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ phone }),
      });
      const data = await res.json();
      if (!res.ok) {
        if (res.status === 429) {
          setErrorMsg('Too many attempts. Please wait before requesting a new code.');
        } else if (res.status === 404) {
          setErrorMsg('No account found for that number. Make sure you use the phone number from checkout.');
        } else {
          setErrorMsg(data.error || 'Something went wrong. Please try again.');
        }
        return;
      }
      setStep('otp');
      setResendCooldown(30);
    } catch {
      setErrorMsg('Unable to send code. Please try again.');
    } finally {
      setLoading(false);
    }
  }

  async function verifyOtp(codeStr) {
    setErrorMsg('');
    setLoading(true);
    try {
      const res = await fetch(`${API}/auth/otp/verify`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ phone, code: codeStr }),
      });
      const data = await res.json();
      if (!res.ok) {
        setErrorMsg(
          res.status === 401
            ? 'Incorrect code. Please check your text and try again.'
            : data.error || 'Verification failed. Please try again.'
        );
        setOtp(['', '', '', '', '', '']);
        document.getElementById('otp-0')?.focus();
        return;
      }
      // Store JWT
      localStorage.setItem('melvo_token', data.token);
      // Route based on onboarding status
      if (data.onboarding_complete === false) {
        navigate('/onboarding');
      } else {
        navigate('/dashboard');
      }
    } catch {
      setErrorMsg('Unable to verify. Please try again.');
    } finally {
      setLoading(false);
    }
  }

  function handleOtpChange(index, value) {
    if (!/^\d*$/.test(value)) return;
    const next = [...otp];
    next[index] = value.slice(-1);
    setOtp(next);
    if (value && index < 5) {
      document.getElementById(`otp-${index + 1}`)?.focus();
    }
    // Auto-verify on last digit
    if (index === 5 && value) {
      const code = [...next].join('');
      if (code.length === 6) verifyOtp(code);
    }
  }

  function handleOtpKeyDown(index, e) {
    if (e.key === 'Backspace' && !otp[index] && index > 0) {
      document.getElementById(`otp-${index - 1}`)?.focus();
    }
  }

  function handleOtpPaste(e) {
    const pasted = e.clipboardData.getData('text').replace(/\D/g, '').slice(0, 6);
    if (pasted.length === 6) {
      const next = pasted.split('');
      setOtp(next);
      document.getElementById('otp-5')?.focus();
      verifyOtp(pasted);
    }
  }

  // ── Render ────────────────────────────────────────────────

  if (step === 'validating') {
    return (
      <div style={styles.page}>
        <div style={styles.card}>
          <span style={styles.logo}>Melvo</span>
          <p style={{ ...styles.subtext, marginBottom: 0 }}>Verifying your link…</p>
        </div>
      </div>
    );
  }

  if (step === 'error') {
    return (
      <div style={styles.page}>
        <div style={styles.card}>
          <span style={styles.logo}>Melvo</span>
          <h1 style={styles.heading}>This link isn't valid</h1>
          <p style={styles.subtext}>{errorMsg}</p>
          <div style={styles.support}>
            Questions? <a href="mailto:hello@melvo.io" style={styles.supportLink}>hello@melvo.io</a>
          </div>
        </div>
      </div>
    );
  }

  if (step === 'phone') {
    return (
      <div style={styles.page}>
        <div style={styles.card}>
          <span style={styles.logo}>Melvo</span>
          <h1 style={styles.heading}>Activate your account</h1>
          <p style={styles.subtext}>
            Enter the mobile number you used at checkout. We'll send a 6-digit code to verify it's you.
          </p>
          {errorMsg && <p style={styles.error}>{errorMsg}</p>}
          <label style={styles.label} htmlFor="phone">Mobile number</label>
          <input
            id="phone"
            type="tel"
            placeholder="+1 (612) 555-0123"
            value={phone}
            onChange={e => setPhone(e.target.value)}
            onKeyDown={e => e.key === 'Enter' && requestOtp()}
            style={styles.input}
            autoFocus
          />
          <button
            onClick={requestOtp}
            disabled={loading}
            style={{ ...styles.button, ...(loading ? styles.buttonDisabled : {}) }}
          >
            {loading ? 'Sending…' : 'Send Code'}
          </button>
        </div>
      </div>
    );
  }

  if (step === 'otp') {
    const formatted = phone.replace(/(\d{3})(\d{3})(\d{4})/, '($1) $2-$3');
    return (
      <div style={styles.page}>
        <div style={styles.card}>
          <span style={styles.logo}>Melvo</span>
          <h1 style={styles.heading}>Check your texts</h1>
          <p style={styles.subtext}>
            We sent a 6-digit code to {formatted}. Enter it below.
          </p>
          {errorMsg && <p style={styles.error}>{errorMsg}</p>}
          <div style={styles.otpRow} onPaste={handleOtpPaste}>
            {otp.map((digit, i) => (
              <input
                key={i}
                id={`otp-${i}`}
                type="text"
                inputMode="numeric"
                maxLength={1}
                value={digit}
                onChange={e => handleOtpChange(i, e.target.value)}
                onKeyDown={e => handleOtpKeyDown(i, e)}
                style={styles.otpInput}
                autoFocus={i === 0}
                disabled={loading}
              />
            ))}
          </div>
          <button
            style={{ ...styles.button, ...(loading ? styles.buttonDisabled : {}) }}
            disabled={loading || otp.join('').length < 6}
            onClick={() => verifyOtp(otp.join(''))}
          >
            {loading ? 'Verifying…' : 'Verify'}
          </button>
          <button
            style={{ ...styles.resend, ...(resendCooldown > 0 ? styles.resendDisabled : {}) }}
            disabled={resendCooldown > 0}
            onClick={() => {
              setOtp(['', '', '', '', '', '']);
              setErrorMsg('');
              setStep('phone');
            }}
          >
            {resendCooldown > 0 ? `Resend in ${resendCooldown}s` : 'Didn\'t get it? Go back'}
          </button>
          <div style={styles.support}>
            Questions? <a href="mailto:hello@melvo.io" style={styles.supportLink}>hello@melvo.io</a>
          </div>
        </div>
      </div>
    );
  }

  return null;
}
